import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import QuestionTitle from "../Title";
import Help from "../Help";
import { getEnglishTranslation } from "../../../../utils/englishI18n";
import { BODY_PARTS, BODY_CONTAINER_ASPECT_RATIO, BODY_SCALE, BODY_OFFSET_X, BODY_OFFSET_Y } from "./bodyLayout";
import bodySrc from "./body.svg";

/**
 * Body-map renderer for MULTIPLE_ANSWER questions with displayOption "body".
 *
 * Renders the anatomical body SVG on a separate layer (tunable via
 * BODY_SCALE / BODY_OFFSET_X/Y in bodyLayout.js) and superimposes
 * transparent/bordered hit-targets with body-part labels over each part.
 *
 * Selection tracking uses the i18n label key suffix (e.g.
 * "surveys.msk.answers.shoulder_left" → "shoulder_left") so that buttons
 * sharing the same translated text never accidentally co-select.
 */

const Root = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    grid-template-rows: 1fr;
    align-items: start;
    width: 100%;
    height: 100%;
    overflow: hidden;
`;

const TitlePanel = styled.div`
    padding: 0.75rem 0.5rem 0 0.75rem;
    text-align: left;
`;

const HelpPanel = styled.div`
    padding: 0.75rem 0.75rem 0 0.5rem;
    text-align: right;
`;

/** Stacks the floating pills vertically, so "No Pain" sits underneath "Other body part". */
const PillStack = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1.25rem;
    margin-top: 2.5rem;
`;

/**
 * Toggle pill for choices that are part of the multiple answer but have no
 * coordinates on the silhouette (e.g. "Other body part"). Rendered in the help
 * panel, sharing the selected/unselected visual language of the map buttons.
 */
const FloatingChoiceButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 1rem 1.75rem;
    border-radius: 999px;
    border: 2px solid ${p => (p.selected ? p.theme.colours.flat.teal.hex : "rgba(30, 45, 61, 0.75)")};
    background: ${p => (p.selected ? p.theme.colours.flat.teal.hex : "rgba(255, 255, 255, 0.92)")};
    box-shadow: ${p => (p.selected ? "0 0 0 3px rgba(62, 189, 178, 0.4)" : "0 2px 6px rgba(0, 0, 0, 0.22)")};
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: background 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
    &:active {
        transform: scale(0.93);
    }
`;

const BodyStage = styled.div`
    position: relative;
    height: 100%;
    aspect-ratio: ${BODY_CONTAINER_ASPECT_RATIO};
    overflow: hidden;
`;

/**
 * Separate body layer — adjust BODY_SCALE / BODY_OFFSET_X/Y in bodyLayout.js
 * to align the SVG with the button grid.
 */
const BodyLayer = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    z-index: 1;
`;

const BodyImg = styled.img`
    /* ─── Body sizing & position ─────────────────────────────────────────────
     * All three tuning constants live in bodyLayout.js so you never need to
     * touch this file:
     *
     *  BODY_SCALE       — controls how tall the SVG is relative to the stage.
     *                     1.0 = fills the full stage height exactly.
     *                     Increase (e.g. 1.15) to make it bigger / zoomed in.
     *                     Decrease (e.g. 0.85) to shrink it and add breathing room.
     *
     *  BODY_OFFSET_X    — horizontal nudge as % of the image's own width.
     *                     0   = perfectly centred.
     *                     +10 = shift right by 10 % of image width.
     *                     -10 = shift left.
     *
     *  BODY_OFFSET_Y    — vertical nudge as % of the image's own height.
     *                     0   = perfectly centred.
     *                     +10 = shift down by 10 % of image height.
     *                     -10 = shift up.
     * ──────────────────────────────────────────────────────────────────────── */
    height: ${BODY_SCALE * 100}%;
    width: auto;
    transform: translate(${BODY_OFFSET_X}%, ${BODY_OFFSET_Y}%);
    pointer-events: none;
    user-select: none;
`;

const shapeCss = ({ shape }) => {
    switch (shape) {
        case "circle":
            return css`
                border-radius: 50%;
                aspect-ratio: 1;
            `;
        case "oval-v":
            return css`
                border-radius: 999px;
                aspect-ratio: 0.6;
            `;
        case "oval-h":
            return css`
                border-radius: 999px;
                aspect-ratio: 1.7;
            `;
        case "pill":
        default:
            return css`
                border-radius: 999px;
                aspect-ratio: 2.2;
            `;
    }
};

const Button = styled.button`
    position: absolute;
    z-index: 2;
    top: ${p => p.y}%;
    left: ${p => p.x}%;
    width: ${p => p.w}%;
    /* tx / ty: per-button translate anchor from BODY_PARTS.
     * Both default to -50% which centres the button on its x/y coordinate.
     * Set tx or ty on a BODY_PARTS entry to shift just that button's anchor:
     *   tx: "-50%"  (default) — horizontally centred on x
     *   ty: "-100%" — bottom edge sits at y (button entirely above)
     *   ty: "0%"    — top edge sits at y (button entirely below)
     *   ty: "-80%"  — mostly above y */
    transform: translate(${p => p.tx || "-50%"}, ${p => p.ty || "-50%"});
    padding: 0;
    margin: 0;
    border: 2px solid ${p => (p.selected ? p.theme.colours.flat.teal.hex : "rgba(30, 45, 61, 0.75)")};
    background: ${p => (p.selected ? p.theme.colours.flat.teal.hex : "rgba(255, 255, 255, 0.92)")};
    box-shadow: ${p => (p.selected ? "0 0 0 3px rgba(62, 189, 178, 0.4)" : "0 2px 6px rgba(0, 0, 0, 0.22)")};
    cursor: pointer;
    /* Suppress the webkit tap highlight rectangle that ignores border-radius */
    -webkit-tap-highlight-color: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
    ${shapeCss}
    &:active {
        transform: translate(${p => p.tx || "-50%"}, ${p => p.ty || "-50%"}) scale(0.93);
    }
`;

const ButtonLabel = styled.span`
    font-size: clamp(0.6rem, 1.2vw, 0.9rem);
    line-height: 1.15;
    font-weight: 700;
    letter-spacing: 0.01em;
    text-align: center;
    user-select: none;
    pointer-events: none;
    white-space: normal;
    overflow: hidden;
    max-width: 90%;
    color: ${p => (p.selected ? "#ffffff" : "#1e2d3d")};
`;

/**
 * Exclusive choice ("No Pain"): selecting it answers the question and advances, so it is
 * styled as an action — the station primary button colour with a forward arrow — rather
 * than as a toggle pill. The label never wraps, so "NO PAIN" stays on one line.
 */
const ExclusiveChoiceButton = styled(FloatingChoiceButton)`
    gap: 0.75rem;
    padding: 1rem 1.75rem 1rem 2rem;
    border: 0;
    background: ${p => (p.selected ? p.theme.colours.flat.darkBlue.hex : p.theme.colours.flat.blue.hex)};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
    &:active {
        background: ${p => p.theme.colours.flat.darkBlue.hex};
    }
`;

const ExclusiveChoiceLabel = styled(ButtonLabel)`
    white-space: nowrap;
    max-width: none;
    font-size: clamp(0.8rem, 1.5vw, 1.1rem);
    color: #ffffff;
`;

// Drawn inline rather than via Icon: icon-map.svg carries its own `path { stroke: black }` rule,
// which page CSS cannot override inside a <use> reference, so any stroke on arrowWithTail renders black.
const ForwardArrowSvg = styled.svg`
    flex-shrink: 0;
    width: 1.1em;
    height: 1.1em;
    [dir="rtl"] & {
        transform: rotate(180deg);
    }
`;

const ForwardArrow = () => (
    <ForwardArrowSvg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path
            d="M2 8h11.5M9 3.5L13.5 8 9 12.5"
            fill="none"
            stroke="#ffffff"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </ForwardArrowSvg>
);

/**
 * Extract the trailing segment of an i18n key and use it as the bodyPartCode.
 * e.g. "surveys.msk.answers.shoulder_left" -> "shoulder_left"
 */
const tail = (key = "") => {
    const parts = String(key).split(".");
    return parts[parts.length - 1] || "";
};

const LABEL_KEY_PREFIX = "surveys.msk.answers.";

/**
 * Choices with a raw (non-namespaced) label, e.g. "No Pain", are exclusive: selecting one
 * clears every other selection and vice versa, and advances the survey. The raw label is kept as the stored answer
 * (it's the English sentinel scoring matches on), so it's displayed via its own i18n key.
 */
const EXCLUSIVE_CHOICE_LABEL_KEYS = {
    "No Pain": "surveys.msk.buttons.no_pain"
};

const isExclusiveChoice = choice => Object.prototype.hasOwnProperty.call(EXCLUSIVE_CHOICE_LABEL_KEYS, choice.label);

const derivePartCode = choice => {
    const candidateKeys = [choice.code, choice.helpText, choice.label].filter(Boolean);
    for (const candidate of candidateKeys) {
        if (typeof candidate !== "string") continue;
        if (candidate.startsWith(LABEL_KEY_PREFIX)) {
            return tail(candidate);
        }
    }
    return "";
};

const BodyMap = ({ id, model, onAnswersChange }) => {
    const { t, i18n } = useTranslation();

    const choices = model.getChoices();
    const title = model.getDisplayText();
    const helpText = model.getHelpText();

    /* build a lookup: bodyPartCode -> choice schema */
    const choiceByPart = {};
    choices.forEach(choice => {
        const code = derivePartCode(choice);
        if (code) choiceByPart[code] = choice;
    });

    // Fallback: if codes cannot be derived from labels/helpText, map by choice order.
    if (Object.keys(choiceByPart).length === 0) {
        BODY_PARTS.forEach((part, index) => {
            if (choices[index]) {
                choiceByPart[part.code] = choices[index];
            }
        });
    }

    /*
     * Track selection by the unique label key (or synthetic identity) so that
     * buttons sharing the same translated text never co-select. The stored
     * answer value emitted to onAnswersChange is the English translation so
     * downstream code sees e.g. "Left shoulder".
     */
    const identityFor = choice => choice.label || choice.helpText || String(choice.code || "");

    const answerValueFor = choice => {
        const key = identityFor(choice);
        return getEnglishTranslation(key, key, i18n);
    };

    const [selectedKeys, setSelectedKeys] = useState([]);

    const toggle = useCallback(
        choice => {
            const id = identityFor(choice);
            let nextKeys;
            if (selectedKeys.includes(id)) {
                nextKeys = selectedKeys.filter(k => k !== id);
            } else if (isExclusiveChoice(choice)) {
                nextKeys = [id];
            } else {
                const exclusiveKeys = choices.filter(isExclusiveChoice).map(identityFor);
                nextKeys = [...selectedKeys.filter(k => !exclusiveKeys.includes(k)), id];
            }
            setSelectedKeys(nextKeys);

            // Map keys → choices → English answer values for downstream.
            const nextAnswers = nextKeys
                .map(k => choices.find(c => identityFor(c) === k))
                .filter(Boolean)
                .map(answerValueFor);
            // Selecting an exclusive choice ("No Pain") answers the question outright.
            const advance = isExclusiveChoice(choice) && nextKeys.includes(id);
            onAnswersChange(nextAnswers, { advance });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedKeys, choices, i18n, onAnswersChange]
    );

    const isSelected = choice => selectedKeys.includes(identityFor(choice));

    /* choices that belong to the multiple answer but have no silhouette coordinates
     * (e.g. "Other body part") render as toggle pills in the help panel instead */
    const mappedPartCodes = new Set(BODY_PARTS.map(part => part.code));
    const floatingChoices = choices.filter(choice => {
        const code = derivePartCode(choice);
        return code && !mappedPartCodes.has(code);
    });
    const exclusiveChoices = choices.filter(isExclusiveChoice);

    const partButtons = BODY_PARTS.map(part => {
        const choice = choiceByPart[part.code];
        if (!choice) return null;
        const selected = isSelected(choice);
        const labelKey = choice.label || choice.helpText || "";
        const labelText = labelKey ? t(labelKey, labelKey) : "";
        return (
            <Button
                key={part.code}
                type="button"
                x={part.x}
                y={part.y}
                w={part.w}
                shape={part.shape}
                tx={part.tx}
                ty={part.ty}
                selected={selected}
                onClick={() => toggle(choice)}
                data-testid={`body-part-${part.code}`}
                aria-label={labelText}
                title={labelText}
                aria-pressed={selected}
            >
                <ButtonLabel selected={selected}>{labelText}</ButtonLabel>
            </Button>
        );
    });

    return (
        <Root id={id} data-testid="survey-question-body-map">
            <TitlePanel>
                <QuestionTitle htmlFor={id}>{t(title)}</QuestionTitle>
            </TitlePanel>
            <BodyStage>
                <BodyLayer>
                    <BodyImg src={bodySrc} alt="" aria-hidden="true" draggable={false} />
                </BodyLayer>
                {partButtons}
            </BodyStage>
            <HelpPanel>
                {helpText && <Help>{t(helpText)}</Help>}
                <PillStack>
                    {floatingChoices.map(choice => {
                        const code = derivePartCode(choice);
                        const selected = isSelected(choice);
                        const labelKey = choice.label || choice.helpText || "";
                        const labelText = labelKey ? t(labelKey, labelKey) : "";
                        return (
                            <FloatingChoiceButton
                                key={code}
                                type="button"
                                selected={selected}
                                onClick={() => toggle(choice)}
                                data-testid={`body-part-${code}`}
                                aria-label={labelText}
                                title={labelText}
                                aria-pressed={selected}
                            >
                                <ButtonLabel selected={selected}>{labelText}</ButtonLabel>
                            </FloatingChoiceButton>
                        );
                    })}
                    {exclusiveChoices.map(choice => {
                        const selected = isSelected(choice);
                        const labelText = t(EXCLUSIVE_CHOICE_LABEL_KEYS[choice.label], choice.label);
                        return (
                            <ExclusiveChoiceButton
                                key={choice.label}
                                type="button"
                                selected={selected}
                                onClick={() => toggle(choice)}
                                data-testid="body-exclusive-choice"
                                aria-label={labelText}
                                title={labelText}
                                aria-pressed={selected}
                            >
                                <ExclusiveChoiceLabel selected={selected}>{labelText}</ExclusiveChoiceLabel>
                                <ForwardArrow />
                            </ExclusiveChoiceButton>
                        );
                    })}
                </PillStack>
            </HelpPanel>
        </Root>
    );
};

BodyMap.propTypes = {
    id: PropTypes.any,
    model: PropTypes.object.isRequired,
    onAnswersChange: PropTypes.func.isRequired
};

export default BodyMap;
